import * as THREE from 'three';

// Combat FX in WORLD space (seen in both views, but authored for the 3rd-person body): brass shell CASINGS that
// eject + tumble + fall, a brief muzzle FLASH sprite, and a SMOKE puff. Fire rate is bounded so plain growing pools
// are fine (no instancing). Add group to the scene once; spawn on each shot; update(dt) each frame.

interface Casing { mesh: THREE.Mesh; vel: THREE.Vector3; spin: THREE.Vector3; age: number; life: number; }
interface Sprite2 { spr: THREE.Sprite; age: number; life: number; s0: number; s1: number; o0: number; }

const GRAVITY = 12;
// Muzzle-flash POINT LIGHT (friend 2026-07-13: "muzzle flash night time illumination, absolute kino").
// ONE reused light pulsed per shot -- cheap, and only reads at night when the world is dark.
const MUZZLE_LIGHT = { color: 0xffd39a, peak: 9, distance: 16, decay: 1.4, life: 0.07 } as const;

export class WeaponFx {
  readonly group = new THREE.Group();
  private readonly casings: Casing[] = [];
  private readonly flashes: Sprite2[] = [];
  private readonly smokes: Sprite2[] = [];
  private readonly casingGeo = new THREE.BoxGeometry(0.02, 0.02, 0.055);
  private readonly casingMat = new THREE.MeshLambertMaterial({ color: 0xc8a23a, flatShading: true }); // brass
  private flashMat: THREE.SpriteMaterial | null = null;
  private smokeMat: THREE.SpriteMaterial | null = null;
  private readonly muzzleLight = new THREE.PointLight(MUZZLE_LIGHT.color, 0, MUZZLE_LIGHT.distance, MUZZLE_LIGHT.decay);
  private muzzleLightAge: number = MUZZLE_LIGHT.life; // >= life = off
  private muzzleLightPeak = 0;

  constructor() {
    this.group.add(this.muzzleLight);
  }

  // Pulse the muzzle POINT LIGHT at a shot (both views). Lights the surroundings for a night gunfight;
  // washed out in daylight. Decays over MUZZLE_LIGHT.life in update().
  muzzleFlashLight(x: number, y: number, z: number): void {
    this.muzzleLight.position.set(x, y, z);
    this.muzzleLightPeak = MUZZLE_LIGHT.peak * (0.85 + Math.random() * 0.3);
    this.muzzleLight.intensity = this.muzzleLightPeak;
    this.muzzleLightAge = 0;
  }

  // Eject a spent casing from (x,y,z) to the gun's right (rx,rz) + up + slightly back, with a random tumble.
  casing(x: number, y: number, z: number, rx: number, rz: number): void {
    let c = this.casings.find((q) => !q.mesh.visible);
    if (!c) { const mesh = new THREE.Mesh(this.casingGeo, this.casingMat); this.group.add(mesh); c = { mesh, vel: new THREE.Vector3(), spin: new THREE.Vector3(), age: 0, life: 1.5 }; this.casings.push(c); }
    const j = () => (Math.random() - 0.5); // small jitter
    c.mesh.visible = true; c.mesh.position.set(x, y, z);
    c.vel.set(rx * (2.4 + Math.random()) + j() * 0.6, 2.2 + Math.random() * 1.2, rz * (2.4 + Math.random()) + j() * 0.6);
    c.spin.set(j() * 22, j() * 22, j() * 22);
    c.age = 0; c.life = 1.4 + Math.random() * 0.3;
  }

  // A brief, bright muzzle flash at the barrel (world space) -- used in 3rd person (FP has its own viewmodel flash).
  flash(x: number, y: number, z: number): void {
    if (!this.flashMat) this.flashMat = makeFlashMaterial();
    let f = this.flashes.find((q) => !q.spr.visible);
    if (!f) { const spr = new THREE.Sprite(this.flashMat.clone()); spr.renderOrder = 5; this.group.add(spr); f = { spr, age: 0, life: 0.06, s0: 0.55, s1: 0.55, o0: 1 }; this.flashes.push(f); }
    f.spr.visible = true; f.spr.position.set(x, y, z); f.spr.scale.setScalar(0.5 + Math.random() * 0.2);
    (f.spr.material as THREE.SpriteMaterial).rotation = Math.random() * Math.PI; (f.spr.material as THREE.SpriteMaterial).opacity = 1; f.age = 0; f.life = 0.06;
  }

  // A small gray smoke puff that grows + fades (barrel smoke on a shot).
  smoke(x: number, y: number, z: number): void {
    if (!this.smokeMat) this.smokeMat = makeSmokeMaterial();
    let s = this.smokes.find((q) => !q.spr.visible);
    if (!s) { const spr = new THREE.Sprite(this.smokeMat.clone()); spr.renderOrder = 4; this.group.add(spr); s = { spr, age: 0, life: 0.5, s0: 0.15, s1: 0.7, o0: 0.4 }; this.smokes.push(s); }
    s.spr.visible = true; s.spr.position.set(x, y + 0.02, z); s.spr.scale.setScalar(s.s0); (s.spr.material as THREE.SpriteMaterial).opacity = s.o0; s.age = 0; s.life = 0.45 + Math.random() * 0.15;
  }

  update(dt: number): void {
    if (this.muzzleLightAge < MUZZLE_LIGHT.life) {
      this.muzzleLightAge += dt;
      const t = Math.min(1, this.muzzleLightAge / MUZZLE_LIGHT.life);
      this.muzzleLight.intensity = this.muzzleLightPeak * (1 - t); // linear fall-off from the shot's peak
    }
    for (const c of this.casings) {
      if (!c.mesh.visible) continue;
      c.age += dt;
      if (c.age >= c.life) { c.mesh.visible = false; continue; }
      c.vel.y -= GRAVITY * dt;
      c.mesh.position.addScaledVector(c.vel, dt);
      c.mesh.rotation.x += c.spin.x * dt; c.mesh.rotation.y += c.spin.y * dt; c.mesh.rotation.z += c.spin.z * dt;
    }
    for (const f of this.flashes) {
      if (!f.spr.visible) continue;
      f.age += dt; if (f.age >= f.life) { f.spr.visible = false; continue; }
      (f.spr.material as THREE.SpriteMaterial).opacity = f.o0 * (1 - f.age / f.life);
    }
    for (const s of this.smokes) {
      if (!s.spr.visible) continue;
      s.age += dt; const t = s.age / s.life; if (t >= 1) { s.spr.visible = false; continue; }
      s.spr.scale.setScalar(s.s0 + (s.s1 - s.s0) * t); (s.spr.material as THREE.SpriteMaterial).opacity = s.o0 * (1 - t);
    }
  }
}

function makeFlashMaterial(): THREE.SpriteMaterial {
  const size = 64, cv = document.createElement('canvas'); cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.4, 'rgba(255,214,120,0.9)'); g.addColorStop(1, 'rgba(255,160,60,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.SpriteMaterial({ map: tex, color: 0xffe0a0, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false });
}

function makeSmokeMaterial(): THREE.SpriteMaterial {
  const size = 64, cv = document.createElement('canvas'); cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(150,150,150,0.85)'); g.addColorStop(0.5, 'rgba(120,120,120,0.45)'); g.addColorStop(1, 'rgba(110,110,110,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.4, depthWrite: false });
}
